import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, Layers, Shuffle, ClipboardCheck } from 'lucide-react';

const LEVELS = [1, 2, 3, 4, 5, 6];
const CSCA_SUBJECTS = [
  { level: 2, name: '中文理科（IPA）' },
  { level: 1, name: '中文文科（IPS）' },
  { level: 3, name: '数学' },
  { level: 4, name: '物理' },
  { level: 5, name: '化学' },
];

type ExamFamily = 'hsk3' | 'csca';

const ExamSystems: React.FC<{ family: ExamFamily; allowedLevels?: number[] }> = ({ family, allowedLevels }) => {
  const isCsca = family === 'csca';
  const allEntries = isCsca
    ? CSCA_SUBJECTS
    : LEVELS.map(level => ({ level, name: `HSK ${level}` }));
  const entries = allowedLevels ? allEntries.filter(entry => allowedLevels.includes(entry.level)) : allEntries;

  const renderLevelCards = (system: 'workbook' | 'simulation' | 'test') => (
    <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${isCsca ? 'lg:grid-cols-5' : 'lg:grid-cols-6'}`}>
      {entries.map(({ level, name }) => {
        const isCscaMathSimulation = isCsca && system === 'simulation' && level === 3;
        const isCscaLanguageSimulation = isCsca && system === 'simulation' && (level === 1 || level === 2);
        const isCscaOpenSimulation = isCscaMathSimulation || isCscaLanguageSimulation;
        const target = isCscaMathSimulation ? '/csca' : isCsca ? `/csca/${system}/${level}` : `/hsk3/${system}/hsk${level}`;
        const cscaSimulationColor = level === 2
          ? { card: 'bg-emerald-50 border-emerald-300 hover:border-emerald-500', icon: 'bg-emerald-100 text-emerald-700' }
          : level === 1
            ? { card: 'bg-orange-50 border-orange-300 hover:border-orange-500', icon: 'bg-orange-100 text-orange-700' }
            : { card: 'bg-indigo-50 border-indigo-300 hover:border-indigo-500', icon: 'bg-indigo-100 text-indigo-700' };
        return (
        <Link
          key={`${system}-${level}`}
          to={target}
          className={`group rounded-2xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
            system === 'workbook'
              ? 'bg-white border-indigo-100 hover:border-indigo-300'
              : isCscaOpenSimulation
                ? `${cscaSimulationColor.card} shadow-sm`
                : 'bg-slate-50 border-slate-200 hover:border-orange-300'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
              system === 'workbook' ? 'bg-indigo-100 text-[#4F46E5]' : isCscaOpenSimulation ? cscaSimulationColor.icon : 'bg-orange-100 text-orange-600'
            }`}>
              {level}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:translate-x-0.5 transition-transform" />
          </div>
          <div className="font-bold text-[#312E81]">{name}</div>
          <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-500">
            <span className={system === 'test' ? 'min-w-0 truncate' : undefined}>{isCsca ? system === 'test' ? `level ${level}` : `level ${level} · ${name}` : '查看题库'}</span>
            {isCsca && system === 'workbook' && level === 3 && <span className="rounded-full bg-green-100 px-2 py-0.5 font-bold text-green-700">已开放</span>}
            {isCscaMathSimulation && <span className="rounded-full bg-green-100 px-2 py-0.5 font-bold text-green-700">已开放</span>}
            {isCscaLanguageSimulation && <span className="rounded-full bg-green-100 px-2 py-0.5 font-bold text-green-700">真题已接入</span>}
            {isCsca && system === 'test' && <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${level === 3 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{level === 3 ? '已开放' : '题库暂未添加'}</span>}
          </div>
        </Link>
      )})}
    </div>
  );

  return (
    <div className="space-y-6">
      <section className="rounded-3xl bg-gradient-to-br from-indigo-50 to-white border-2 border-indigo-200 p-5 md:p-7 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#4F46E5] text-white flex items-center justify-center shadow-md shadow-indigo-500/20">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl md:text-2xl font-bold text-[#312E81]">Workbook</h2>
                <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-bold text-[#4F46E5]">{isCsca ? '数学已开放' : '常用'}</span>
              </div>
              <p className="text-sm text-gray-600 mt-0.5">{isCsca ? 'CSCA 五科独立 Workbook 题库' : '按固定章节和固定题目学习'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 bg-white/80 border border-indigo-100 rounded-xl px-3 py-2 self-start md:self-auto">
            <Layers className="w-4 h-4" />
            {isCsca ? 'CSCA Workbook' : '独立 Workbook 体系'}
          </div>
        </div>
        {renderLevelCards('workbook')}
      </section>

      <section className="rounded-3xl bg-white border border-slate-200 p-5 md:p-7">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center">
            <Shuffle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800">Simulation</h2>
            <p className="text-sm text-gray-500 mt-0.5">{isCsca ? '选择 CSCA 科目进入考试模式' : '从 Simulation 题库随机组卷'}</p>
          </div>
        </div>
        {renderLevelCards('simulation')}
      </section>

      {isCsca && <section className="rounded-3xl bg-white border border-slate-200 p-5 md:p-7" data-testid="csca-test-row">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><ClipboardCheck className="w-6 h-6" /></div>
          <div><h2 className="text-xl md:text-2xl font-bold text-slate-800">Test</h2><p className="text-sm text-gray-500 mt-0.5">Tes awal untuk mengetahui kemampuan dasar sebelum belajar.</p></div>
        </div>
        {renderLevelCards('test')}
      </section>}
    </div>
  );
};

export const CSCASystems: React.FC<{ allowedLevels?: number[] }> = ({ allowedLevels }) => <ExamSystems family="csca" allowedLevels={allowedLevels} />;

const HSK30Systems: React.FC<{ allowedLevels?: number[] }> = ({ allowedLevels }) => <ExamSystems family="hsk3" allowedLevels={allowedLevels} />;

export default HSK30Systems;
